/**
 * Route Optimization Types
 * Traveling Salesman Problem (TSP) solver with constraints
 */

export type DrivingProfile = 'eco' | 'fast' | 'balanced'

export interface DrivingProfileConfig {
  id: DrivingProfile
  name: string
  description: string
  avgSpeedKmh: number
  fuelEfficiency: number // L/100km multiplier (1.0 = baseline, 0.9 = 10% better)
  restFrequencyKm: number // Pause every X km
  restDurationMin: number
  maxDailyDrivingHours: number
}

export interface RouteConstraints {
  maxDetourKm?: number // Max detour allowed for POI suggestions
  maxDetourPercent?: number // Max detour as % of original route (e.g., 15 = 15%)
  budgetEur?: number
  maxDurationDays?: number
  preferScenic?: boolean
  avoidTolls?: boolean
  avoidHighways?: boolean
  profile: DrivingProfile
}

export interface OptimizationResult {
  originalOrder: string[] // Stage IDs
  optimizedOrder: string[] // Stage IDs (reordered)
  savings: {
    distanceKm: number
    durationHours: number
    fuelLiters: number
    costEur: number
    co2Kg: number
  }
  comparison: {
    original: RouteMetrics
    optimized: RouteMetrics
  }
  alternatives: RouteAlternative[]
}

export interface RouteMetrics {
  totalDistanceKm: number
  totalDurationHours: number
  fuelLiters: number
  fuelCostEur: number
  co2Kg: number
  tollCostEur?: number
}

export interface RouteAlternative {
  id: string
  name: string
  order: string[] // Stage IDs
  metrics: RouteMetrics
  description: string
}

export interface DetourRecommendation {
  poiId: string
  name: string
  category: string
  location: {
    lat: number
    lng: number
  }
  insertAfterStageId: string
  detourKm: number
  detourMinutes: number
  detourPercent: number
  score: number // 0-100 (relevance based on user preferences)
  reason: string
}

export interface TSPRequest {
  planId: string
  stageIds: string[] // Must have at least 2 stages
  constraints: RouteConstraints
}

export interface TSPResponse {
  planId: string
  result: OptimizationResult
  detourRecommendations: DetourRecommendation[]
  computationTimeMs: number
}

export interface CompareProfilesRequest {
  planId: string
  stageIds: string[]
  profiles: DrivingProfile[]
}

export interface CompareProfilesResponse {
  comparisons: Array<{
    profile: DrivingProfile
    metrics: RouteMetrics
    estimatedArrival: string // ISO date
  }>
}

// Pre-configured driving profiles
export const DRIVING_PROFILES: Record<DrivingProfile, DrivingProfileConfig> = {
  eco: {
    id: 'eco',
    name: 'Éco',
    description: 'Conduite économique et écologique',
    avgSpeedKmh: 90,
    fuelEfficiency: 0.85, // 15% better fuel economy
    restFrequencyKm: 150,
    restDurationMin: 20,
    maxDailyDrivingHours: 7,
  },
  balanced: {
    id: 'balanced',
    name: 'Équilibré',
    description: 'Compromis temps/économie',
    avgSpeedKmh: 110,
    fuelEfficiency: 1, // Baseline
    restFrequencyKm: 200,
    restDurationMin: 15,
    maxDailyDrivingHours: 8,
  },
  fast: {
    id: 'fast',
    name: 'Rapide',
    description: 'Arriver le plus vite possible',
    avgSpeedKmh: 130,
    fuelEfficiency: 1.15, // 15% worse fuel economy
    restFrequencyKm: 250,
    restDurationMin: 10,
    maxDailyDrivingHours: 10,
  },
}

// Fuel & CO2 constants
export const FUEL_CONSTANTS = {
  FUEL_PRICE_EUR_PER_L: 1.85, // Average diesel/gasoline price
  AVG_CONSUMPTION_L_PER_100KM: 6.5, // Baseline consumption
  CO2_KG_PER_L: 2.3, // CO2 emissions per liter
  TOLL_EUR_PER_100KM: 8, // Average toll cost
}
